// client/src/coms/voip/voiceState.js
import {
  getLocalSpeakingNow,
  getLocalSpeakingLevel,
  getRemoteSpeakingState,
  getLocalMicMuted,
  getLocalDeafened,
  getRemoteVoiceStatus,
  getRemoteVoiceAnimation,
  getLocalVoiceAnimation,
} from './voiceClient';

function looksLikeJwt(s) {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  if (!t) return false;
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(t);
}

function pickFirstJwtFromObject(obj, depth = 0) {
  if (!obj || depth > 6) return '';
  if (typeof obj === 'string') return looksLikeJwt(obj) ? obj.trim() : '';

  if (Array.isArray(obj)) {
    for (const v of obj) {
      const found = pickFirstJwtFromObject(v, depth + 1);
      if (found) return found;
    }
    return '';
  }

  if (typeof obj !== 'object') return '';

  const directKeys = ['token', 'jwt', 'access_token', 'accessToken'];
  for (const k of directKeys) {
    if (looksLikeJwt(obj[k])) return String(obj[k]).trim();
  }

  const nestedKeys = ['data', 'result', 'payload', 'body'];
  for (const k of nestedKeys) {
    const found = pickFirstJwtFromObject(obj[k], depth + 1);
    if (found) return found;
  }

  for (const key of Object.keys(obj)) {
    const found = pickFirstJwtFromObject(obj[key], depth + 1);
    if (found) return found;
  }

  return '';
}

function pickUrl(obj) {
  if (!obj) return '';
  const url = typeof obj.url === 'string' ? obj.url.trim() : '';
  if (url) return url;
  if (obj.data && typeof obj.data.url === 'string') return obj.data.url.trim();
  if (obj.result && typeof obj.result.url === 'string') return obj.result.url.trim();
  return '';
}

function pickRoom(obj) {
  if (!obj) return '';
  const room = typeof obj.room === 'string' ? obj.room.trim() : '';
  if (room) return room;
  if (obj.data && typeof obj.data.room === 'string') return obj.data.room.trim();
  if (obj.result && typeof obj.result.room === 'string') return obj.result.room.trim();
  return '';
}

function tryParseJsonFromText(text) {
  if (typeof text !== 'string') return null;
  const t = text.trim();
  if (!t) return null;
  if (!(t.startsWith('{') || t.startsWith('['))) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function parseIdsFromRoomName(roomName) {
  const rn = String(roomName || '').trim();
  const m = rn.match(/^coms_(\d+)_(\d+)$/);
  if (!m) return { serverDbIdFromRoom: '', channelDbIdFromRoom: '' };
  return { serverDbIdFromRoom: m[1] || '', channelDbIdFromRoom: m[2] || '' };
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickParticipantConnected(p, fallback = true) {
  if (!p || typeof p !== 'object') return !!fallback;
  if (typeof p.isConnected === 'boolean') return p.isConnected;
  if (typeof p.connected === 'boolean') return p.connected;
  if (typeof p.state === 'string') {
    const s = String(p.state).toLowerCase();
    if (s.includes('disconnect')) return false;
    if (s.includes('connect')) return true;
  }
  return !!fallback;
}

function pickParticipantSpeaking(p) {
  if (!p || typeof p !== 'object') return false;

  if (typeof p.isSpeaking === 'boolean') return p.isSpeaking;
  if (typeof p.is_speaking === 'boolean') return p.is_speaking;
  if (typeof p.speaking === 'boolean') return p.speaking;

  const level = toNumberOrZero(
    p.audioLevel != null
      ? p.audioLevel
      : p.audio_level != null
        ? p.audio_level
        : p.level != null
          ? p.level
          : 0
  );

  return level > 0.02;
}

function pickParticipantAudioLevel(p) {
  if (!p || typeof p !== 'object') return 0;
  return toNumberOrZero(
    p.audioLevel != null
      ? p.audioLevel
      : p.audio_level != null
        ? p.audio_level
        : p.level != null
          ? p.level
          : 0
  );
}

function pickParticipantVoiceAnimation(p, fallback = 'flash') {
  if (!p || typeof p !== 'object') return fallback;
  const raw =
    p.voiceAnimation != null
      ? p.voiceAnimation
      : p.voice_animation != null
        ? p.voice_animation
        : p.active_voice_animation != null
          ? p.active_voice_animation
          : p.animation != null
            ? p.animation
            : fallback;
  const next = String(raw || '').trim();
  return next || fallback;
}

function extractNumericIdFromValue(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/(\d+)$/);
  return m && m[1] ? m[1] : '';
}

function tryParseParticipantMetadata(metadata) {
  const raw = String(metadata || '').trim();
  if (!raw) return null;
  if (!(raw.startsWith('{') || raw.startsWith('['))) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function extractParticipantUserId(p) {
  if (!p || typeof p !== 'object') return '';

  const directCandidates = [
    p.userId,
    p.user_id,
    p.userID,
    p.id,
    p.identity,
    p.sid,
  ];

  for (const value of directCandidates) {
    const hit = extractNumericIdFromValue(value);
    if (hit) return hit;
  }

  const metadataObj = tryParseParticipantMetadata(p.metadata);
  if (metadataObj && typeof metadataObj === 'object') {
    const metadataCandidates = [
      metadataObj.userId,
      metadataObj.user_id,
      metadataObj.id,
      metadataObj.identity,
      metadataObj.sid,
      metadataObj.user && metadataObj.user.id,
      metadataObj.user && metadataObj.user.user_id,
    ];
    for (const value of metadataCandidates) {
      const hit = extractNumericIdFromValue(value);
      if (hit) return hit;
    }
  }

  return '';
}

export async function fetchVoiceToken(arg) {
  const roomName = typeof arg === 'string' ? String(arg || '').trim() : String(arg?.roomName || '').trim();
  const serverId = typeof arg === 'object' && arg ? String(arg.serverId ?? '') : '';
  const channelId = typeof arg === 'object' && arg ? String(arg.channelId ?? '') : '';
  const channelDbId = typeof arg === 'object' && arg ? String(arg.channelDbId ?? '') : '';

  if (!roomName) throw new Error('voice_room_missing');

  if (!/^coms_\d+_\d+$/.test(roomName)) {
    throw new Error(`voice_room_invalid:${roomName}`);
  }

  const { serverDbIdFromRoom, channelDbIdFromRoom } = parseIdsFromRoomName(roomName);

  const body = {
    room: roomName,
    roomName,
    serverId,
    channelId,
    channelDbId,
    serverDbId: serverDbIdFromRoom,
    channelDbIdFromRoom,
  };

  const res = await fetch('/api/coms/voice/token', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  let text = '';
  try {
    text = await res.text();
  } catch {
    text = '';
  }

  const data = tryParseJsonFromText(text);

  if (!res.ok) {
    const detailFromJson =
      data && (data.error || data.detail || data.message)
        ? String(data.error || data.detail || data.message)
        : '';

    const detailFromText = text ? text.slice(0, 800) : '';
    const msg =
      detailFromJson
        ? `voice_token_failed_${res.status}:${detailFromJson}`
        : detailFromText
          ? `voice_token_failed_${res.status}:${detailFromText}`
          : `voice_token_failed_${res.status}`;

    const err = new Error(msg);
    err.status = res.status;
    err.request = { url: '/api/coms/voice/token', body };
    err.response = { text, json: data };
    throw err;
  }

  const url = pickUrl(data) || (typeof data === 'string' ? '' : '');
  const token = pickFirstJwtFromObject(data);
  const resolvedRoom = pickRoom(data);

  if (!url) throw new Error(`voice_token_missing_url:${text.slice(0, 200)}`);
  if (!token) throw new Error(`voice_token_missing_token:${text.slice(0, 200)}`);

  return { url, token, room: resolvedRoom, raw: data };
}

export function snapshotParticipants(room) {
  const out = [];

  if (room && room.localParticipant) {
    const localSpeaking = getLocalSpeakingNow();
    const localLevel = getLocalSpeakingLevel();
    const lkLevel = pickParticipantAudioLevel(room.localParticipant);
    const audioLevel = localLevel > 0 ? localLevel : lkLevel;
    const localUserId = extractParticipantUserId(room.localParticipant);

    out.push({
      id: 'local',
      userId: localUserId || '',
      user_id: localUserId || '',
      name: room.localParticipant.name || room.localParticipant.identity || 'User',
      identity: room.localParticipant.identity || '',
      sid: room.localParticipant.sid || '',
      local: true,
      connected: pickParticipantConnected(room.localParticipant, true),
      muted: !!getLocalMicMuted(),
      deafened: !!getLocalDeafened(),
      isSpeaking: localSpeaking || pickParticipantSpeaking(room.localParticipant),
      audioLevel,
      voiceAnimation: getLocalVoiceAnimation(),
      voice_animation: getLocalVoiceAnimation(),
      active_voice_animation: getLocalVoiceAnimation(),
    });
  }

  try {
    room &&
      room.remoteParticipants &&
      room.remoteParticipants.forEach((p) => {
        const override = getRemoteSpeakingState(p.identity || '');
        const overrideIsSpeaking =
          override && typeof override.isSpeaking === 'boolean'
            ? override.isSpeaking
            : null;

        const overrideAudioLevel =
          override && Number.isFinite(Number(override.audioLevel))
            ? Number(override.audioLevel)
            : null;

        const voiceStatus = getRemoteVoiceStatus(p.identity || '');
        const voiceAnimationState = getRemoteVoiceAnimation(p.identity || '');
        const remoteUserId = extractParticipantUserId(p);

        out.push({
          id: p.sid || p.identity || p.name || 'remote',
          userId: remoteUserId || '',
          user_id: remoteUserId || '',
          name: p.name || p.identity || 'User',
          identity: p.identity || '',
          sid: p.sid || '',
          local: false,
          connected: pickParticipantConnected(p, true),
          muted: !!(voiceStatus && voiceStatus.muted),
          deafened: !!(voiceStatus && voiceStatus.deafened),
          isSpeaking: overrideIsSpeaking != null ? overrideIsSpeaking : pickParticipantSpeaking(p),
          audioLevel: overrideAudioLevel != null ? overrideAudioLevel : pickParticipantAudioLevel(p),
          voiceAnimation: pickParticipantVoiceAnimation(
            voiceAnimationState && typeof voiceAnimationState.voiceAnimation === 'string'
              ? { voiceAnimation: voiceAnimationState.voiceAnimation }
              : p,
            'flash'
          ),
          voice_animation: pickParticipantVoiceAnimation(
            voiceAnimationState && typeof voiceAnimationState.voiceAnimation === 'string'
              ? { voiceAnimation: voiceAnimationState.voiceAnimation }
              : p,
            'flash'
          ),
          active_voice_animation: pickParticipantVoiceAnimation(
            voiceAnimationState && typeof voiceAnimationState.voiceAnimation === 'string'
              ? { voiceAnimation: voiceAnimationState.voiceAnimation }
              : p,
            'flash'
          ),
        });
      });
  } catch {}

  return out;
}